import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom"; // Import ReactDOM for portals
import useDropdownData from "../../fetches/loadDropDownData";
import { parseDropdownData } from "../../utils/parseDropdownData";
import { Square, CheckSquare } from "lucide-react";
import SearchBar from "../SearchBar";
import Button from "../Buttons/Button";
import { Back } from "../../icons/Back";

/**
 * GenericDropdown Component (Portalled)
 *
 * A sophisticated dropdown component for selecting items, now using React Portals
 * to avoid z-index issues.
 * - Search functionality to filter items
 * - Temporary selection with confirmation workflow (OK/Cancel)
 * - Reset option to clear selection
 * - Click outside to close behavior
 * - Loading and error states
 * - Visual indicators for selected items
 * - Configurable data source (companies, teams, departments, positions)
 *
 * @param {Object} props - Component props
 * @param {string|number|null} props.selectedValue - Currently selected item ID
 * @param {Function} props.onChange - Callback function called when selection is confirmed
 * @param {Function} props.onClose - Callback function called when dropdown closes
 * @param {string} props.dataType - Type of data to fetch ('companies'|'teams'|'departments'|'positions')
 * @param {string} props.placeholder - Placeholder text for empty states
 * @param {string} props.resetText - Text for reset button (defaults to "Сбросить")
 *
 * @component
 * @since 1.0.1 (Portalled version)
 */
const GenericDropdown = ({
  selectedValue,
  onChange,
  onClose,
  dataType = "companies",
  placeholder = "Select Item",
  resetText = "Сбросить",
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSelectedValue, setTempSelectedValue] = useState(selectedValue);

  const dropdownMenuRef = useRef(null); // Ref for the portalled dropdown content
  const searchBarRef = useRef(null);
  const placeholderRef = useRef(null); // Ref for the in-flow placeholder element
  
  const [positionStyle, setPositionStyle] = useState({});

  const { dropdownData, error, loading } = useDropdownData();
  const parsedData = dropdownData ? parseDropdownData(dropdownData) : null;
  const allItems =
    parsedData && Array.isArray(parsedData[dataType])
      ? parsedData[dataType]
      : [];
  const filteredItems = allItems.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    setTempSelectedValue(selectedValue);
  }, [selectedValue]);

  // Calculate position for the portalled dropdown based on the placeholder
  useEffect(() => {
    if (isOpen && placeholderRef.current) {
      const rect = placeholderRef.current.getBoundingClientRect();
      setPositionStyle({
        position: 'absolute',
        top: `${rect.top + window.scrollY}px`,
        left: `${rect.left + window.scrollX}px`,
        // width: `${rect.width}px`, // Let w-max handle width, or set explicitly if needed
        zIndex: 10000, // High z-index for portalled content
      });
    }
  }, [isOpen]); // Recalculate if isOpen changes. For dynamic positioning on scroll/resize, more complex listeners would be needed.

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    setTempSelectedValue(selectedValue); // Revert to original selected value
    if (onClose) {
      onClose();
    }
  }, [selectedValue, onClose]); // Dependencies for useCallback

  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        dropdownMenuRef.current &&
        !dropdownMenuRef.current.contains(event.target) &&
        (!searchBarRef.current || !searchBarRef.current.contains(event.target))
        // No need to check placeholderRef here, as it's not part of the interactive menu
      ) {
        handleCancel(); // Close dropdown and revert changes
      }
    };
    if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, handleCancel]);


  const handleTemporarySelect = (itemId) => {
    setTempSelectedValue(itemId);
  };

  const handleConfirmSelection = () => {
    if (onChange) {
      onChange(tempSelectedValue);
    }
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const handleResetSelection = () => {
    setTempSelectedValue(null);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };
  
  // Ensure a portal root element exists in the document body
  const portalRootId = "generic-dropdown-portal-root";
  useEffect(() => {
    let portalRoot = document.getElementById(portalRootId);
    if (!portalRoot) {
      portalRoot = document.createElement("div");
      portalRoot.id = portalRootId;
      document.body.appendChild(portalRoot);
    }
    // Optional: Cleanup portal root if component unmounts, though usually kept for app lifetime
    // return () => { if (portalRoot && portalRoot.parentNode) portalRoot.parentNode.removeChild(portalRoot); };
  }, []);


  if (!isOpen) {
    return null;
  }

  const portalRootElement = typeof window !== 'undefined' ? document.getElementById(portalRootId) : null;

  // The actual visual content of the dropdown
  const dropdownView = (
    <div
      ref={dropdownMenuRef} // Attach ref to the main portalled element
      className="mt-5 p-3 w-max rounded-md shadow-md bg-white" // Original classes
      style={positionStyle} // Apply calculated absolute position and z-index
      onClick={(e) => e.stopPropagation()} // Prevent clicks inside from closing if not handled by specific buttons
    >
      {/* Main dropdown content container */}
      <div className="w-max p-3 bg-[#EAEFF3] rounded-md max-h-[calc(60px+200px)] overflow-auto">
        {/* Search bar section */}
        <div ref={searchBarRef} onClick={(e) => e.stopPropagation()}>
          <SearchBar variant="compact" onSearch={handleSearch} />
        </div>

        {/* Items list section */}
        <div className="my-2">
          {loading && (
            <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
          )}
          {error && (
            <div className="px-3 py-2 text-sm text-red-500">
              Error loading {dataType}.
            </div>
          )}
          {!loading && !error && filteredItems.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">
              {searchQuery
                ? `No ${dataType} found matching your search.`
                : `No ${dataType} found.`}
            </div>
          )}
          {!loading &&
            !error &&
            filteredItems.map((item) => {
              const isSelected = tempSelectedValue === item.id;
              return (
                <label
                  key={item.id}
                  className="flex items-center justify-between py-1 cursor-pointer rounded hover:bg-gray-100"
                  onClick={(e) => {
                    // e.stopPropagation(); // Already on parent, but can be here too
                    handleTemporarySelect(item.id);
                  }}
                >
                  <div className="flex items-center p-2">
                    {isSelected ? (
                      <CheckSquare
                        className="rounded-md"
                        stroke="#0832DE"
                        width={20}
                        height={20}
                        strokeWidth={1.5}
                      />
                    ) : (
                      <Square
                        className="rounded-md"
                        stroke="#0832DE"
                        width={20}
                        height={20}
                        strokeWidth={1}
                      />
                    )}
                    <span className="px-1 text-gray-700 text-sm">
                      {String(item.name)}
                    </span>
                  </div>
                </label>
              );
            })}
        </div>

        {/* Action buttons section */}
        <div className="flex justify-center gap-1 pt-2 border-gray-300">
          <Button
            onClick={handleCancel}
            className="flex items-center justify-center px-4 py-2 bg-[#CED5E5] text-[#2B3674] w-32 h-8 text-sm font-semibold border-none rounded-lg cursor-pointer transition-colors duration-200 ease-in-out focus:outline-none hover:bg-[#B8C5D9]"
            text="Cancel"
          />
          <Button
            onClick={handleConfirmSelection}
            className="flex items-center justify-center px-4 py-2 bg-[#0832DE] text-white w-20 h-8 text-sm font-semibold border-none rounded-lg cursor-pointer transition-colors duration-200 ease-in-out focus:outline-none hover:bg-[#0625B8]"
            text="Ok"
          />
        </div>
      </div>

      {/* Reset selection button */}
      <Button
        onClick={handleResetSelection}
        className="flex items-center justify-start w-full mt-2 px-4 py-2 bg-white text-[#2B3674] text-base border-none rounded-lg cursor-pointer transition-colors duration-200 ease-in-out focus:outline-none hover:bg-gray-50 gap-1"
      >
        <Back /> {resetText}
      </Button>
    </div>
  );

  // Render an invisible placeholder in the normal flow to determine position
  // and portal the actual dropdown content.
  return (
    <>
      <div ref={placeholderRef} style={{ width: 0, height: 0, margin:0, padding:0, border: 'none', visibility: 'hidden' }} />
      {isOpen && portalRootElement && ReactDOM.createPortal(dropdownView, portalRootElement)}
    </>
  );
};

export default GenericDropdown;